import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spin, message, Card, List, Tag, Typography } from 'antd';
import {
    getAssignedObservations, // For Closure notifications (Assigned/Rejected - for the person doing the closure)
    getUserAssignedAudits, // For Assigned Audit notifications & Submitted Observation notifications (for the auditor/verifier)
} from '../services/auditAPI'; // Assuming these APIs are in auditAPI.js
import { Link } from 'react-router-dom'; // For navigation links

const { Title, Text } = Typography;

const NotificationPage = () => {
    const { user } = useSelector((state) => state.profile);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Define the polling interval (e.g., every 30 seconds)
    const POLLING_INTERVAL = 30000; // 30 seconds

    const fetchNotifications = async () => {
        if (!user?._id) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        let fetchedNotifications = [];

        try {
            // Fetch assigned observations for Closure notifications (relevant for the person assigned the closure task)
            // This API should return observations where the current user is the responsible person for closure.
            const assignedObservationsData = await getAssignedObservations(user._id);
            // console.log('Assigned Observations Data (from getAssignedObservations):', assignedObservationsData);

            // Process assigned observations (for closure task assignee)
            assignedObservationsData.forEach(audit => {
                // Ensure audit.assignedObservations exists and is an array before processing
                if (audit.assignedObservations && Array.isArray(audit.assignedObservations)) {
                    audit.assignedObservations.forEach(assignedObs => {
                        // --- Handle Closure Task Assigned notifications (Status 'Assigned') ---
                        if (assignedObs.closureData?.status === 'Assigned') {
                            // Check if the assigned task also has a rejection reason
                            if (assignedObs.closureData?.rejectionReason) {
                                // This task was previously rejected and is now reassigned
                                fetchedNotifications.push({
                                    id: `${assignedObs._id}-assigned-rejected`, // Unique ID
                                    type: 'Closure Task Assigned (Rejected)', // Indicate it was rejected
                                    title: `Closure task reassigned (Rejected): ${audit.auditTitle}`,
                                    description: `Observation: ${assignedObs.observation}`,
                                    auditId: audit.auditId, // Assuming auditId is available here
                                    observationId: assignedObs._id,
                                    rejectionReason: assignedObs.closureData.rejectionReason, // Include rejection reason
                                });
                            } else {
                                // This is a standard assigned closure task
                                fetchedNotifications.push({
                                    id: `${assignedObs._id}-assigned`, // Unique ID
                                    type: 'Closure Task Assigned',
                                    title: `Closure task assigned for Audit: ${audit.auditTitle}`,
                                    description: `Observation: ${assignedObs.observation}`,
                                    auditId: audit.auditId, // Assuming auditId is available here
                                    observationId: assignedObs._id,
                                });
                            }
                        }
                        // Note: 'Observation Submitted' for verification is handled below from getUserAssignedAudits
                    });
                }
            });

            // Fetch assigned audits (relevant for the auditor/manager who needs to see audit progress and submissions)
            // This API should return audits where the current user is part of the audit team.
            const userAssignedAudits = await getUserAssignedAudits(user._id);
            // console.log('User Assigned Audits Data (from getUserAssignedAudits):', userAssignedAudits);

            // Process assigned audits (for audit team member)
            userAssignedAudits.forEach(assignedAudit => {
                // --- Handle Assigned Audit notifications (Status not 'Completed') ---
                if (assignedAudit.status !== 'Completed') {
                    fetchedNotifications.push({
                        id: `${assignedAudit._id}-audit-assigned`, // Unique ID
                        type: 'Assigned Audit',
                        title: `Audit assigned: ${assignedAudit.title}`,
                        description: `Status: ${assignedAudit.status}`,
                        auditId: assignedAudit._id,
                        // Add other relevant details like deadline, team members, etc.
                    });
                }

                // --- Handle Observation Submitted for Verification notifications ---
                // Iterate through the report of the assigned audit
                 if (assignedAudit.report && Array.isArray(assignedAudit.report)) {
                    assignedAudit.report.forEach(observationInReport => {
                        // Check if the observation has closure data and the status is 'Submitted'
                        if (observationInReport.closureData?.status === 'Submitted') {
                             fetchedNotifications.push({
                                id: `${assignedAudit._id}-${observationInReport._id}-submitted`, // Unique ID
                                type: 'Observation Submitted for Verification', // Clearer type
                                title: `Observation submitted for verification in Audit: ${assignedAudit.title}`,
                                description: `Observation: ${observationInReport.observation}`,
                                auditId: assignedAudit._id,
                                observationId: observationInReport._id,
                                submittedBy: observationInReport.closureData?.submittedBy, // Assuming submittedBy field exists in closureData
                                // Add other relevant details like submission date, submitted evidence, etc.
                             });
                        }
                        // Add checks for other statuses like 'Verified', 'Rejected' within the report if needed for auditor notifications
                        // e.g., Notify auditor when an observation they are verifying is rejected or verified.
                    });
                 }
            });


            setNotifications(fetchedNotifications);

        } catch (err) {
            console.error("Failed to fetch notifications:", err);
            setError('Failed to load notifications.');
            message.error('Failed to load notifications.');
        } finally {
            setLoading(false);
        }
    };

    // Effect for initial fetch and setting up polling
    useEffect(() => {
        // Initial fetch
        fetchNotifications();

        // Set up polling if user is available
        let pollingIntervalId;
        if (user?._id) {
            pollingIntervalId = setInterval(() => {
                fetchNotifications();
            }, POLLING_INTERVAL);
        }

        // Cleanup function to clear the interval when the component unmounts
        // or when user changes (which triggers a re-run of this effect)
        return () => {
            if (pollingIntervalId) {
                clearInterval(pollingIntervalId);
            }
        };
    }, [user?._id]); // Dependency: user ID ensures effect re-runs if user changes

    // Function to render different notification types
    const renderNotificationItem = (item) => {
        let tagColor;
        let linkPath;
        let linkText;
        let extraContent = null; // To hold additional info like submittedBy or rejectionReason

        switch (item.type) {
            case 'Closure Task Assigned':
                tagColor = 'blue';
                // Link to the page where the user can perform the closure task
                linkPath = `/audit/verify`; // Example path, adjust as needed
                linkText = 'Complete Closure Task';
                break;
            case 'Closure Task Assigned (Rejected)':
                tagColor = 'orange';
                 // Link to the page where the user can perform the closure task, highlighting rejection
                linkPath = `/audit/verify`; // Example path, adjust as needed
                linkText = 'Review and Complete Reassigned Task';
                 if (item.rejectionReason) {
                    extraContent = <p><Text strong>Reason for Rejection:</Text> {item.rejectionReason}</p>;
                 }
                break;
            case 'Observation Submitted for Verification': // Handle the new type
                 tagColor = 'purple';
                 // Link to the page where the user can verify the submission (e.g., the audit report or a specific verification page)
                 linkPath = `/audit/${item.auditId}`; // Example path, adjust as needed
                 linkText = 'Review Submission for Verification';
                 if (item.submittedBy) {
                    extraContent = <p><Text strong>Submitted By:</Text> {item.submittedBy}</p>;
                 }
                 // You might add submission date or link to evidence here
                 break;
            case 'Assigned Audit':
                tagColor = 'green';
                // Link to the main audit details page
                linkPath = `/audit/${item.auditId}`; // Adjust path as needed
                linkText = 'View Assigned Audit';
                break;
            default:
                tagColor = 'gray';
                linkPath = '#';
                linkText = 'View Details';
        }

        return (
            <List.Item key={item.id}>
                <Card
                    title={<Text strong>{item.title}</Text>}
                    bordered={false}
                    style={{ width: '100%' }}
                    extra={<Tag color={tagColor}>{item.type}</Tag>}
                >
                    <p>{item.description}</p>
                    {/* Display extra content like rejection reason or submitted by */}
                    {extraContent}
                    {/* Add links to navigate */}
                    <Link className='text-blue-500' to={linkPath}>{linkText}</Link>
                </Card>
            </List.Item>
        );
    };

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>Notifications</Title>

            {loading ? (
                <div style={{ textAlign: 'center', marginTop: '50px' }}>
                    <Spin size="large" tip="Loading notifications..." />
                </div>
            ) : error ? (
                <div style={{ color: 'red', textAlign: 'center', marginTop: '50px' }}>
                    {error}
                </div>
            ) : notifications.length === 0 ? (
                <div style={{ textAlign: 'center', marginTop: '50px', color: '#888' }}>
                    No new notifications.
                </div>
            ) : (
                <List
                    itemLayout="vertical"
                    dataSource={notifications}
                    renderItem={renderNotificationItem}
                />
            )}
        </div>
    );
};

export default NotificationPage;
